import { FC } from "react";
import { useNavigate } from "react-router-dom";
import adminNavigation from "@/config/navigation/admin-navigation.json";
import * as Icons from "lucide-react";
import { UserRole } from "@/types/user";

export interface AdminNavigationItem {
  id: string | number;
  icon: FC<{ className?: string }>;
  label: string;
  route: string;
  section: string;
}

export interface AdminNavigationSection {
  title: string;
  items: AdminNavigationItem[];
}

function getAdminUser(): { role?: UserRole; organisationId?: string } | null {
  try {
    const userStr = localStorage.getItem("admin_user");
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
}

export function useAdminNavigation() {
  const navigate = useNavigate();
  const adminUser = getAdminUser();
  const role = adminUser?.role ?? null;
  const isSuperAdmin = role === UserRole.SUPERADMIN;
  const isCorporateAdmin = role === UserRole.CORPORATE_ADMIN;

  const navigationSections: Record<string, AdminNavigationSection> = {
    main: { title: "Main", items: [] },
    management: { title: "Management", items: [] },
    content: { title: "Content", items: [] },
    marketing: { title: "Marketing", items: [] },
    system: { title: "System", items: [] },
  };

  Object.entries(adminNavigation).forEach(([id, config]: [string, any]) => {
    if (config.superAdminOnly && !isSuperAdmin) return;
    if (config.maatraAdminOnly && isCorporateAdmin) return;
    if (config.corporateAdminOnly && !isCorporateAdmin) return;

    const section = config.section || "main";
    if (navigationSections[section]) {
      const IconComponent = Icons[config.icon as keyof typeof Icons] as FC<{
        className?: string;
      }>;
      navigationSections[section].items.push({
        id: Number(id),
        icon: IconComponent,
        label: config.label,
        route: config.route,
        section,
      });
    }
  });

  const navigateTo = (route: string) => navigate(route);

  return { navigationSections, navigateTo, isSuperAdmin };
}

export default useAdminNavigation;
